import React from 'react';
import PropTypes from 'prop-types';
import { NetworkStatus } from '@apollo/client';
import { useIntl, defineMessages } from 'react-intl';
import DocumentEmptyStateImage from 'images/DocumentEmptyState.png';
import useGetCompanyDocuments from '../templates/documents/useGetCompanyDocuments';
import useAddFromCompanyDocuments from './useAddFromCompanyDocuments';
import MultiSelectModal from 'components/templates/shared/MultiSelectModal';
import TemplateEmptyState from 'components/templates/shared/TemplateEmptyState';

const messages = defineMessages({
  manageDocuments: {
    id: 'Documents.companyDocument.manageDocuments',
    defaultMessage: 'Manage Documents',
    description: 'Label for Manage Documents button',
  },
  emptyCompanyDocumentMsg: {
    id: 'Documents.companyDocument.emptyMsg',
    defaultMessage: 'Streamline your projects with Project Documents',
    description:
      'Message for users when viewing an empty project documents list',
  },
  addDocument: {
    id: 'Documents.companyDocument.addDocumentButton',
    defaultMessage: 'Add Document',
    description: 'Add Document button',
  },
  addDocuments: {
    id: 'Documents.companyDocument.addsDocuments',
    defaultMessage: 'Add Documents',
    description: 'Add Documents button',
  },
  addToProject: {
    id: 'Documents.companyDocument.addToProject',
    defaultMessage: 'Add to Project',
    description: 'Add to Project button',
  },
});

const CompanyDocumentsModal = ({
  onRequestClose,
  projectId,
  onDocumentsAdded,
}) => {
  const { formatMessage } = useIntl();

  const { loading, companyDocuments, fetchMore, networkStatus, hasNextPage } =
    useGetCompanyDocuments();

  const { addFromCompanyDocuments, loading: addLoading } =
    useAddFromCompanyDocuments({
      projectId,
      onSuccess: () => {
        onRequestClose();
        onDocumentsAdded();
      },
    });

  return (
    <MultiSelectModal
      isOpen
      onRequestClose={onRequestClose}
      footerContentBefore={{
        label: formatMessage(messages.manageDocuments),
        onClick: () => window.Turbo.visit('/templates/documents'),
      }}
      emptyState={
        <TemplateEmptyState
          message={formatMessage(messages.emptyCompanyDocumentMsg)}
          imageSource={DocumentEmptyStateImage}
          action={
            !companyDocuments.length
              ? null
              : {
                  label: formatMessage(messages.addDocument),
                  disabled: false,
                  onClick: () => {
                    window.Turbo.visit('/templates/documents');
                  },
                }
          }
        />
      }
      loading={
        (loading && networkStatus !== NetworkStatus.fetchMore) || addLoading
      }
      templates={companyDocuments}
      hasNextPage={hasNextPage}
      fetchMore={fetchMore}
      onAddTemplates={(documentBlueprintIds) => {
        addFromCompanyDocuments({ documentBlueprintIds });
      }}
      title={formatMessage(messages.addDocuments)}
      primaryActionLabel={formatMessage(messages.addToProject)}
    />
  );
};

CompanyDocumentsModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  onDocumentsAdded: PropTypes.func,
};

export default CompanyDocumentsModal;
